import { Box } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { memo, ReactElement, useCallback, useState } from 'react';
import { WrapperCardWithTitle } from '../../layouts/WrapperCardWithTitle';
import { ApiError } from '../ApiLoadingGuard/ApiLoadingGuard';
import { BoundingBox, ImageCard } from '../Documentation/ImageCard';
import { ImageZoomCard } from '../Image/ImageZoomCard';
import { DefaultErrorScreen, DefaultLoadingScreen } from '../ApiLoadingGuard';
import { ResourceStatus } from '../../api/resources/useOcInsightsEvents';
import { DefaultWaitingScreen } from '../ApiLoadingGuard/DefaultWaitingScreen';

interface DetailImageQueryGuardedProps {
  title: string;
  imageQuery: UseQueryResult<string, ApiError>;
  downloadFileName: string;
  boundingBoxes?: BoundingBox[];
}

export const DetailImageQueryGuarded = memo(function ({
  title,
  imageQuery: image,
  downloadFileName,
  boundingBoxes,
}: DetailImageQueryGuardedProps): ReactElement {
  const { isPending, isError, isEnabled, data } = image;

  const isNotAvailable = isError || !isEnabled;

  if (isNotAvailable) {
    return (
      <WrapperCardWithTitle title={title} sx={{ flex: 'none', height: '350px' }}>
        <DefaultErrorScreen defaultText={'error.imageNotAvailable'} iconSize='medium' />
      </WrapperCardWithTitle>
    );
  }

  if (isPending) {
    return (
      <WrapperCardWithTitle title={title} sx={{ flex: 'none', height: '350px' }}>
        <DefaultLoadingScreen />
      </WrapperCardWithTitle>
    );
  }

  return (
    <DetailImage imageSrc={data} title={title} downloadFileName={downloadFileName} boundingBoxes={boundingBoxes} />
  );
});

interface LiveDataDetailImageQueryGuardedProps {
  title: string;
  imageQuery: UseQueryResult<string, ApiError>;
  downloadFileName: string;
  boundingBoxes?: BoundingBox[];
  imageStatus: ResourceStatus;
}

export function LiveDataDetailImageQueryGuarded({
  title,
  imageQuery,
  downloadFileName,
  imageStatus,
  boundingBoxes,
}: LiveDataDetailImageQueryGuardedProps) {
  const isNotAvailableYet = imageStatus === 'started';

  if (imageStatus === 'not-started') {
    return (
      <WrapperCardWithTitle title={title} sx={{ flex: 'none', height: '350px' }}>
        <DefaultWaitingScreen text='dataLoading.idle' />
      </WrapperCardWithTitle>
    );
  }

  if (isNotAvailableYet) {
    return (
      <WrapperCardWithTitle title={title} sx={{ flex: 'none', height: '350px' }}>
        <DefaultLoadingScreen text='dataLoading.processing' />
      </WrapperCardWithTitle>
    );
  }

  return (
    <DetailImageQueryGuarded
      title={title}
      imageQuery={imageQuery}
      downloadFileName={downloadFileName}
      boundingBoxes={boundingBoxes}
    />
  );
}

function DetailImage({
  title,
  imageSrc,
  downloadFileName,
  boundingBoxes,
}: {
  title: string;
  imageSrc: string;
  downloadFileName: string;
  boundingBoxes?: BoundingBox[];
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = useCallback(() => {
    if (!imageSrc) {
      return;
    }
    setIsModalOpen(true);
  }, [imageSrc]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <>
      <Box onClick={handleOpenModal} sx={{ cursor: !imageSrc ? 'default' : 'zoom-in' }}>
        <WrapperCardWithTitle title={title} sx={{ flex: 'none', height: '350px' }}>
          <ImageCard imageSrc={imageSrc} downloadFileName={downloadFileName} boundingBoxes={boundingBoxes} />
        </WrapperCardWithTitle>
      </Box>
      <ImageZoomCard isOpen={isModalOpen} onClose={handleCloseModal}>
        <Box height='200px' padding={2}>
          <ImageCard imageSrc={imageSrc} boundingBoxes={boundingBoxes} />
        </Box>
      </ImageZoomCard>
    </>
  );
}
